import { memo } from 'react';
import styles from './StatsPanel.module.css';
import { ratioName } from '../config/metricsConfig';
import { getCompanyLogoUrl } from '../../../config/dataProvider';

// `null`/`undefined` means "not available" (FMP omitted the profile field, or
// the figure isn't computable for this symbol/metric) and must render
// distinctly from a real value, never coerced to 0 or a blank-looking cell —
// the same convention Screener.js's formatCell follows.
const DASH = '—';

// Turn an ISO 3166-1 alpha-2 country code (the backend sends the issuer's
// domicile as "US"/"DE"/"JP" — see get_profile_meta in lambda.py) into its
// flag emoji by mapping each letter to its Unicode regional-indicator symbol
// (offset 0x1F1E6 from 'A'). Two regional indicators combine into a flag glyph.
// Anything that isn't exactly two ASCII letters (unexpected value, a full name,
// null) yields no flag, so the code renders on its own. The row shows the code
// first with the flag trailing it ("US 🇺🇸").
const countryFlag = (code) => {
  if (typeof code !== 'string') return '';
  const cc = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  const base = 0x1f1e6;
  return String.fromCodePoint(
    base + (cc.charCodeAt(0) - 65),
    base + (cc.charCodeAt(1) - 65),
  );
};

const StatsPanel = ({
  companyName,
  symbol,
  metric = 'eps',
  valueBasis = 'marketCap',
  sector = null,
  industry = null,
  country = null,
  currentRatio = null,
  dividendYield = null,
}) => {
  // The label swaps with the selected metric (P/E -> P/FCF -> EV/EBITDA under
  // the Enterprise Value basis) so the row reads e.g. "P/FCF  11.9".
  const valuationLabel = ratioName(metric, valueBasis);
  // A non-positive multiple isn't a meaningful valuation figure (negative or
  // near-zero denominator), so it reads as unavailable rather than as a
  // number — matching how the chart legend omits its bracketed figure.
  const valuationText = currentRatio != null && currentRatio > 0
    ? currentRatio.toFixed(1)
    : DASH;
  const dividendYieldText = dividendYield != null && dividendYield > 0
    ? `${dividendYield.toFixed(2)}%`
    : DASH;

  return (
    <div className={styles.statistics}>
      <div className={styles.companyCard}>
        <div className={styles.companyCardHeader}>
          {/* Company logo is gated behind data-provider licensing/branding
              clearance (see config/dataProvider.js). getCompanyLogoUrl returns
              null while disabled — no logo renders, no hotlink to the
              provider's asset host, and the identity block below still shows
              the ticker + name. */}
          {symbol && getCompanyLogoUrl(symbol.toUpperCase()) && (
            <img
              src={getCompanyLogoUrl(symbol.toUpperCase())}
              alt=""
              className={styles.companyLogo}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <div className={styles.companyIdentity}>
            <span className={styles.companySymbol}>{symbol?.toUpperCase()}</span>
            <h2 className={styles.companyName}>{companyName}</h2>
          </div>
        </div>
        <section className={styles.keyFacts} aria-labelledby="key-facts-heading">
          <h3 id="key-facts-heading" className={styles.keyFactsHeading}>Key Facts</h3>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Sector</span>
            {/* Subsector (FMP's `industry`, e.g. "Consumer Electronics")
                stacked above the GICS sector (e.g. "Technology"), mirroring
                the symbol-above-name identity block: a small muted line on
                top, the primary value below. Falls back to a dash when the
                profile omits either (null != 0 convention). */}
            <span className={styles.sectorValue}>
              {industry && (
                <span className={styles.subsector}>{industry}</span>
              )}
              <span className={styles.detailValue}>{sector || DASH}</span>
            </span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Country</span>
            <span className={styles.detailValue}>
              {country ? `${country}${countryFlag(country) ? ` ${countryFlag(country)}` : ''}` : DASH}
            </span>
          </div>
          {/*
            Label tracks the metric currently selected in ControlsPanel, so it
            swaps with it (P/E -> P/FCF -> EV/EBITDA under the Enterprise Value
            basis) and the value holds just the multiple.
          */}
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>{valuationLabel}</span>
            <span className={styles.detailValue}>{valuationText}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Dividend Yield</span>
            <span className={styles.detailValue}>{dividendYieldText}</span>
          </div>
        </section>
      </div>
    </div>
  );
};

export default memo(StatsPanel);
